import type { Restaurant } from '@/types'

/**
 * Owner-app side menu items an admin can switch off per restaurant (admin Feature List
 * screen). Everything is on unless its key is in the restaurant's `disabledFeatures`.
 * Keys must stay in sync with restaurant-analytics-backend/src/utils/features.ts.
 */
export interface FeatureItem {
  key: string
  label: string
  href?: string
  children?: FeatureItem[]
}

export const RESTAURANT_FEATURES: FeatureItem[] = [
  { key: 'dashboard', label: 'Dashboard', href: '/dashboard' },
  { key: 'analytics', label: 'Analytics', href: '/analytics' },
  { key: 'customers', label: 'Customers', href: '/customers' },
  { key: 'category-analysis', label: 'Category Analysis', href: '/category-analysis' },
  { key: 'sales-reports', label: 'Sales Reports', href: '/sales-reports' },
  { key: 'online-orders', label: 'Online Orders', href: '/online-orders' },
  { key: 'parcel-takeaway', label: 'Parcel & Takeaway', href: '/parcel-takeaway' },
  { key: 'profit-analysis', label: 'Profit Analysis', href: '/profit-analysis' },
  { key: 'hourly-distribution', label: 'Hourly Distribution', href: '/hourly-distribution' },
  { key: 'cancellations', label: 'Cancellations', href: '/cancellations' },
  { key: 'edited-bills', label: 'Edited Bills', href: '/edited-bills' },
  { key: 'menu-performance', label: 'Menu Performance', href: '/menu-performance' },
  { key: 'payment-modes', label: 'Payment Modes', href: '/payment-modes' },
  { key: 'gst-report', label: 'GST Report', href: '/gst-report' },
  { key: 'discounts', label: 'Discounts & Round-off', href: '/discounts' },
  { key: 'staff-performance', label: 'Staff Performance', href: '/staff-performance' },
  { key: 'table-performance', label: 'Table & Area', href: '/table-performance' },
  { key: 'audit-trail', label: 'Audit Trail', href: '/audit-trail' },
  { key: 'receivables', label: 'Receivables', href: '/receivables' },
  { key: 'delivery-performance', label: 'Delivery Performance', href: '/delivery-performance' },
  { key: 'z-report', label: 'Z-Report', href: '/z-report' },
  { key: 'expenses', label: 'Expenses', href: '/expenses' },
  {
    key: 'inventory',
    label: 'Inventory',
    children: [
      { key: 'purchase-report', label: 'Purchase Report', href: '/purchase-report' },
      { key: 'profit-report', label: 'Profit Report', href: '/profit-report' },
      { key: 'indent-reports', label: 'Indent Reports', href: '/indent-reports' },
      { key: 'pnl-report', label: 'P&L Report', href: '/pnl-report' },
    ],
  },
  { key: 'subscription', label: 'Subscription', href: '/subscription' },
]

/**
 * A feature is available unless it, or the group it sits in, is switched off.
 * While the restaurant hasn't loaded yet (null) everything counts as available, so
 * the menu doesn't flash empty.
 */
export function isFeatureEnabled(restaurant: Pick<Restaurant, 'disabledFeatures'> | null | undefined, key: string): boolean {
  const disabled = restaurant?.disabledFeatures
  if (!disabled || disabled.length === 0) return true
  if (disabled.includes(key)) return false
  const parent = RESTAURANT_FEATURES.find(f => f.children?.some(c => c.key === key))
  return !(parent && disabled.includes(parent.key))
}

/** The first enabled page, used as the landing route when the default one is switched off. */
export function getFirstEnabledPath(restaurant: Pick<Restaurant, 'disabledFeatures'> | null | undefined): string | null {
  for (const feature of RESTAURANT_FEATURES) {
    if (feature.children) {
      const child = feature.children.find(c => isFeatureEnabled(restaurant, c.key))
      if (child?.href) return child.href
    } else if (feature.href && isFeatureEnabled(restaurant, feature.key)) {
      return feature.href
    }
  }
  return null
}
